/**
 * Grant queries and mutations for TanStack Query (Vendor App)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPost, queryPut, queryDelete } from '../utils/request'

// Type definitions
export interface GrantResponse {
  id: number
  tenant_id: number | null
  company_group_id: number | null
  description: string | null
  role_id: number
  is_active: boolean
  is_master: boolean
}

export interface GrantListParams {
  page?: number
  per_page?: number
  user_id?: number
  [key: string]: any
}

export interface GrantListResponse {
  items: GrantResponse[]
  total?: number
  page?: number
  per_page?: number
}

export interface GrantCreate {
  email: string
  role_id: number
  company_group_id: number | null
  tenant_id: number | null
}

export interface GrantUpdate {
  role_id: number
  is_active: boolean
  company_group_id: number | null
}

// Cache configuration
const CACHE_TIMES = {
  GRANTS: 5 * 60 * 1000, // 5 minutes
  GRANT_DETAIL: 2 * 60 * 1000, // 2 minutes
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch a list of grants
 */
export const useGrantsQuery = (params: MaybeRefOrGetter<GrantListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.grantList(toValue(params))),
    queryFn: async (): Promise<GrantListResponse> => {
      const queryParams = toValue(params)
      return queryGet<GrantListResponse>('/v2/grants/', { params: queryParams })
    },
    staleTime: CACHE_TIMES.GRANTS,
  })
}

/**
 * Query hook to fetch a single grant by ID
 */
export const useGrantQuery = (grantId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(grantId)
      return id ? queryKeys.grant(id) : ['grants', 'null']
    }),
    queryFn: async (): Promise<GrantResponse> => {
      const id = toValue(grantId)
      if (!id) throw new Error('Grant ID is required')
      return queryGet<GrantResponse>(`/v2/grants/${id}`)
    },
    staleTime: CACHE_TIMES.GRANT_DETAIL,
    enabled: computed(() => !!toValue(grantId)),
  })
}

/**
 * MUTATIONS
 */

/**
 * Mutation hook to create a new grant
 */
export const useCreateGrantMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: GrantCreate): Promise<GrantResponse> => {
      return queryPost<GrantResponse>('/v2/grants/', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.grants() })
      queryClient.invalidateQueries({ queryKey: queryKeys.users() })
    },
  })
}

/**
 * Mutation hook to update an existing grant
 */
export const useUpdateGrantMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ grantId, data }: { grantId: number; data: GrantUpdate }): Promise<GrantResponse> => {
      return queryPut<GrantResponse>(`/v2/grants/${grantId}`, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.grants() })
      queryClient.invalidateQueries({ queryKey: queryKeys.grant(variables.grantId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.users() })
    },
  })
}

/**
 * Mutation hook to delete a grant
 */
export const useDeleteGrantMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (grantId: number): Promise<void> => {
      return queryDelete<void>(`/v2/grants/${grantId}`)
    },
    onSuccess: (_, grantId) => {
      queryClient.removeQueries({ queryKey: queryKeys.grant(grantId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.grants() })
      queryClient.invalidateQueries({ queryKey: queryKeys.users() })
    },
  })
}

/**
 * Mutation hook to revoke a grant (sets is_active = false)
 */
export const useRevokeGrantMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (grantId: number): Promise<void> => {
      return queryPost<void>(`/v2/grants/${grantId}/revoke`, {})
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.grants() })
      queryClient.invalidateQueries({ queryKey: queryKeys.users() })
    },
  })
}
